import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loading, Notify } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaDownload, FaExpand, FaExternalLinkAlt, FaMinus, FaPlus, FaRedo, FaTimes, FaUpload } from 'react-icons/fa'
import { apiUpload } from '../../../../helpers/apiRequester'
import styles from '../shared/mhlShared.module.scss'
import type { AudiogramDoc } from '../shared/mhlTypes'
import { formatBytes, formatDateTime } from '../shared/mhlFormatting'
import { AUDIOGRAM_ACCEPT, validateAudiogramFile } from '../components/AudiologyFileInput/AudiologyFileInput'

interface Props {
    caseRef: string
    doc: AudiogramDoc | null
    canUpload: boolean
    onUploaded?: () => Promise<void> | void
    /** Rendered inside the review modal beneath the image (e.g. the assessment form). */
    reviewAside?: React.ReactNode
    emptyCopy?: string
    previewAriaLabel?: string
}

const isImage = (mime: string | null | undefined) => !!mime && /^image\/(png|jpe?g)$/i.test(mime)
const isPdf = (mime: string | null | undefined) => mime === 'application/pdf'

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3]

/** Compact audiogram card with a full-screen review modal (zoom / rotate). */
export const AudiogramViewer = ({
    caseRef,
    doc,
    canUpload,
    onUploaded,
    reviewAside,
    emptyCopy = 'No audiogram has been uploaded for this client.',
    previewAriaLabel = 'Audiogram review',
}: Props) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [busy, setBusy] = useState(false)
    const [open, setOpen] = useState(false)
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
            if (e.key === '+' || e.key === '=') zoomBy(1)
            if (e.key === '-') zoomBy(-1)
            if (e.key.toLowerCase() === 'r') setRotation((r) => (r + 90) % 360)
        }
        document.addEventListener('keydown', onKey)
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prev
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const zoomBy = (dir: 1 | -1) =>
        setZoom((z) => {
            const i = ZOOM_STEPS.findIndex((s) => s >= z - 1e-6)
            const next = Math.min(ZOOM_STEPS.length - 1, Math.max(0, (i === -1 ? ZOOM_STEPS.length - 1 : i) + dir))
            return ZOOM_STEPS[next]
        })

    const openModal = () => {
        setZoom(1)
        setRotation(0)
        setOpen(true)
    }

    const upload = async (file: File) => {
        const problem = validateAudiogramFile(file)
        if (problem) {
            Report.warning('Invalid file', problem, 'Okay')
            return
        }
        setBusy(true)
        Loading.circle('Uploading audiogram…')
        try {
            const fd = new FormData()
            fd.append('case_ref', caseRef)
            fd.append('audiogram', file, file.name)
            const res = await apiUpload('/military-hearing-loss-upload-audiogram', fd)
            if (res.data?.success) {
                Notify.success('Audiogram uploaded')
                await onUploaded?.()
            } else {
                Report.failure('Upload failed', res.data?.message || 'Could not upload the audiogram', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Upload failed', err?.response?.data?.message || err?.message || 'Could not upload the audiogram', 'Okay')
        } finally {
            Loading.remove()
            setBusy(false)
        }
    }

    const previewable = !!doc && (isImage(doc.mime) || isPdf(doc.mime))

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept={AUDIOGRAM_ACCEPT}
                style={{ display: 'none' }}
                onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void upload(f)
                    e.target.value = ''
                }}
            />

            {doc ? (
                <div className={styles.audioCard}>
                    <button
                        type="button"
                        className={styles.audioThumb}
                        onClick={previewable ? openModal : undefined}
                        disabled={!previewable}
                        aria-label={previewable ? 'Open audiogram for review' : 'Preview unavailable'}
                        title={previewable ? 'Open for review' : `${doc.mime || 'This file type'} cannot be previewed`}
                    >
                        {isImage(doc.mime) ? (
                            <img src={doc.url} alt="" />
                        ) : isPdf(doc.mime) ? (
                            <iframe title="Audiogram thumbnail" src={`${doc.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} tabIndex={-1} />
                        ) : (
                            <span className={styles.audioThumbFallback}>{(doc.fileName.split('.').pop() || 'file').toUpperCase()}</span>
                        )}
                        {previewable && (
                            <span className={styles.audioThumbOverlay}>
                                <FaExpand size={14} /> Review
                            </span>
                        )}
                    </button>

                    <div className={styles.audioCardBody}>
                        <div className={styles.stackCell}>
                            <span className={styles.docName}>{doc.fileName}</span>
                            <span className={styles.docMeta}>
                                {[doc.mime, formatBytes(doc.size), doc.uploadedAt ? `uploaded ${formatDateTime(doc.uploadedAt)}` : null, doc.uploadedBy]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </span>
                        </div>
                        <div className={styles.audioCardActions}>
                            {previewable && (
                                <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`} onClick={openModal}>
                                    <FaExpand size={10} /> Open for review
                                </button>
                            )}
                            <a className={`${styles.btn} ${styles.btnSmall}`} href={doc.url} target="_blank" rel="noreferrer">
                                <FaExternalLinkAlt size={10} /> New tab
                            </a>
                            <a className={`${styles.btn} ${styles.btnSmall}`} href={doc.url} download={doc.fileName}>
                                <FaDownload size={10} /> Download
                            </a>
                            {canUpload && (
                                <button type="button" className={`${styles.btn} ${styles.btnSmall}`} onClick={() => inputRef.current?.click()} disabled={busy}>
                                    <FaUpload size={10} /> Replace
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.emptyStateSmall} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span>{emptyCopy}</span>
                    {canUpload && (
                        <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`} onClick={() => inputRef.current?.click()} disabled={busy}>
                            <FaUpload size={10} /> Upload audiogram
                        </button>
                    )}
                </div>
            )}

            {open &&
                doc &&
                createPortal(
                    <div className={styles.lightbox} role="dialog" aria-modal="true" aria-label={previewAriaLabel} onClick={() => setOpen(false)}>
                        <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.lightboxBar}>
                                <div className={styles.stackCell} style={{ minWidth: 0 }}>
                                    <span className={styles.lightboxTitle}>{doc.fileName}</span>
                                    <span className={styles.lightboxMeta}>
                                        {[doc.mime, formatBytes(doc.size), doc.uploadedAt ? `uploaded ${formatDateTime(doc.uploadedAt)}` : null].filter(Boolean).join(' · ')}
                                    </span>
                                </div>
                                <div className={styles.lightboxTools}>
                                    {isImage(doc.mime) && (
                                        <>
                                            <button type="button" className={styles.lightboxTool} onClick={() => zoomBy(-1)} aria-label="Zoom out" title="Zoom out (−)">
                                                <FaMinus size={11} />
                                            </button>
                                            <span className={styles.lightboxZoom}>{Math.round(zoom * 100)}%</span>
                                            <button type="button" className={styles.lightboxTool} onClick={() => zoomBy(1)} aria-label="Zoom in" title="Zoom in (+)">
                                                <FaPlus size={11} />
                                            </button>
                                            <button type="button" className={styles.lightboxTool} onClick={() => setRotation((r) => (r + 90) % 360)} aria-label="Rotate" title="Rotate (R)">
                                                <FaRedo size={11} />
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.lightboxTool}
                                                onClick={() => {
                                                    setZoom(1)
                                                    setRotation(0)
                                                }}
                                                title="Reset view"
                                            >
                                                Reset
                                            </button>
                                        </>
                                    )}
                                    <a className={styles.lightboxTool} href={doc.url} target="_blank" rel="noreferrer" title="Open in new tab">
                                        <FaExternalLinkAlt size={11} />
                                    </a>
                                    <a className={styles.lightboxTool} href={doc.url} download={doc.fileName} title="Download">
                                        <FaDownload size={11} />
                                    </a>
                                    <button type="button" className={`${styles.lightboxTool} ${styles.lightboxClose}`} onClick={() => setOpen(false)} aria-label="Close" title="Close (Esc)">
                                        <FaTimes size={13} />
                                    </button>
                                </div>
                            </div>

                            <div className={styles.lightboxBody}>
                                <div className={styles.lightboxStage}>
                                    {isImage(doc.mime) ? (
                                        <img src={doc.url} alt="Client audiogram" style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }} className={styles.lightboxImage} />
                                    ) : (
                                        <iframe title="Client audiogram" src={`${doc.url}#toolbar=1&view=FitH`} className={styles.lightboxFrame} />
                                    )}
                                </div>
                                {reviewAside && <aside className={styles.lightboxAside}>{reviewAside}</aside>}
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    )
}
